/**
 * Verdict Findings Module
 *
 * Shared logic for generating teacher-friendly findings from edit metrics.
 * Used by both the web app and Chrome extension.
 *
 * This module is framework-agnostic - it returns string icon identifiers
 * that consumers map to their own icon implementations.
 */

import type { ExtensionEditMetrics, RiskLevel } from "./schemas";

/**
 * Icon identifiers for findings.
 * Consumers map these to their own icon implementations.
 */
export type FindingIcon =
  | "clipboard" // For paste-related findings
  | "clock" // For time-related findings
  | "pencil" // For typing/editing findings
  | "calendar" // For session/day findings
  | "alert"; // For warning findings

/**
 * A finding to display to the user.
 * Framework-agnostic - uses string icon identifier.
 */
export interface Finding {
  /** Icon identifier for the finding */
  icon: FindingIcon;
  /** Main text of the finding (e.g., "78% of content was pasted") */
  headline: string;
  /** Supporting detail text (e.g., "Most content appeared instantly") */
  detail: string;
}

/**
 * Input for generating findings.
 * Simplified compared to web app - doesn't require KeyMoments.
 */
export interface FindingsInput {
  /** Document metrics from API */
  metrics: ExtensionEditMetrics;
  /** Risk level from API */
  riskLevel: RiskLevel;
  /** Total word count */
  wordCount: number;
  /** Number of unique days the document was edited (optional) */
  uniqueDays?: number;
  /** Total number of edit events (optional, for detecting insufficient data) */
  totalEvents?: number;
}

/**
 * Verdict configuration for display.
 */
export interface VerdictConfig {
  /** Display label (e.g., "Suspicious Activity") */
  label: string;
  /** Detail text explaining the verdict */
  detail: string;
  /** Icon identifier for the verdict badge */
  icon: FindingIcon;
  /** Semantic color type for styling */
  colorType: "error" | "warning" | "success" | "neutral";
}

/**
 * Verdict configurations by risk level.
 */
export const VERDICT_CONFIGS: Record<RiskLevel, VerdictConfig> = {
  high: {
    label: "Suspicious Activity",
    detail: "This paper shows unusual writing patterns",
    icon: "alert",
    colorType: "error",
  },
  medium: {
    label: "Unusual Patterns",
    detail: "This paper shows unusual writing patterns",
    icon: "alert",
    colorType: "warning",
  },
  low: {
    label: "Normal Writing",
    detail: "This paper shows typical student writing",
    icon: "pencil",
    colorType: "success",
  },
  none: {
    label: "No Edit History",
    detail: "No editing activity was recorded for this document",
    icon: "alert",
    colorType: "neutral",
  },
};

/**
 * Convert milliseconds to minutes.
 */
function msToMinutes(ms: number): number {
  return Math.round(ms / 60000);
}

/**
 * Estimate expected typing time based on word count.
 * Average typing speed is about 40 words per minute for most students.
 */
function getExpectedTypingMinutes(wordCount: number): number {
  const avgWpm = 40;
  return Math.round(wordCount / avgWpm);
}

/**
 * Generate teacher-friendly findings based on metrics.
 * Returns 2-3 findings prioritized by importance.
 *
 * @param input - The metrics, risk level, and word count
 * @returns Array of 0-3 findings sorted by importance
 */
export function generateFindings(input: FindingsInput): Finding[] {
  const { metrics, riskLevel, wordCount, uniqueDays = 1, totalEvents } = input;
  const findings: Finding[] = [];

  // Check for very few edits (hasInsufficientData equivalent)
  // Threshold: <10 events on 50+ word doc - this is a strong signal
  const eventCount = totalEvents ?? metrics.totalEvents ?? 0;
  if (eventCount > 0 && eventCount < 10 && wordCount >= 50) {
    findings.push({
      icon: "alert",
      headline: "Few edits recorded",
      detail: "Documents written in the editor typically show many more edits",
    });
  }

  // For zero events (riskLevel "none"), return early without adding a finding
  // The verdict section already displays the "No Edit History" message via VERDICT_CONFIGS
  if (riskLevel === "none" || eventCount === 0) {
    return findings;
  }

  const pasteRatio = metrics.paste.pasteToTypeRatio ?? 0;
  const errorCorrectionRate = metrics.editBehavior.errorCorrectionRate ?? 0;
  const activeMinutes = msToMinutes(metrics.session.activeWritingDurationMs);
  const expectedMinutes = getExpectedTypingMinutes(wordCount);
  const sessionCount = metrics.session.sessionCount;
  const largestPasteChars = metrics.paste.largestPasteSize ?? 0;

  // Estimate words from characters (average word length ~5 chars + space)
  const largestPasteWords = Math.round(largestPasteChars / 6);

  // High paste ratio (>50%)
  if (pasteRatio > 0.5) {
    const pastePercent = Math.round(pasteRatio * 100);
    findings.push({
      icon: "clipboard",
      headline: `${pastePercent}% of content was pasted`,
      detail:
        pasteRatio > 0.8
          ? "Most content appeared instantly, not typed"
          : "Content appeared faster than typical typing",
    });
  }

  // Large single paste (>200 words)
  if (largestPasteWords > 200) {
    findings.push({
      icon: "clipboard",
      headline: `${largestPasteWords} words pasted at once`,
      detail: "The largest paste was most of the paper",
    });
  }

  // Low active typing time (suspicious)
  if (
    (riskLevel === "high" || riskLevel === "medium") &&
    activeMinutes > 0 &&
    activeMinutes < expectedMinutes * 0.5
  ) {
    findings.push({
      icon: "clock",
      headline: `Only ${activeMinutes} min of active typing`,
      detail: "Very little time spent writing",
    });
  }

  // Multiple sessions over multiple days (positive for normal papers)
  if (riskLevel === "low" && (sessionCount > 3 || uniqueDays > 1)) {
    const count = uniqueDays > 1 ? uniqueDays : sessionCount;
    findings.push({
      icon: "calendar",
      headline: `Written over ${count} ${uniqueDays > 1 ? "days" : "sessions"}`,
      detail: "Multiple writing sessions spread out",
    });
  }

  // Normal typing time (positive for normal papers)
  if (
    riskLevel === "low" &&
    activeMinutes >= expectedMinutes * 0.5 &&
    findings.length < 2
  ) {
    findings.push({
      icon: "pencil",
      headline: `${activeMinutes} min of active writing`,
      detail: `Reasonable time for ${wordCount.toLocaleString()} words`,
    });
  }

  // Error correction rate insights
  if (errorCorrectionRate > 0.2 && findings.length < 3) {
    findings.push({
      icon: "pencil",
      headline: "Fixed many typos while writing",
      detail: "Typical of natural writing",
    });
  } else if (
    errorCorrectionRate < 0.05 &&
    riskLevel !== "low" &&
    findings.length < 3
  ) {
    findings.push({
      icon: "alert",
      headline: "Almost no typos fixed",
      detail: "Unusual for handwritten content",
    });
  }

  // Return top 3 findings
  return findings.slice(0, 3);
}

/**
 * Get verdict configuration for a risk level.
 */
export function getVerdictConfig(riskLevel: RiskLevel): VerdictConfig {
  return VERDICT_CONFIGS[riskLevel];
}
